import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EmptyState } from '../shared/components/EmptyState';
import { Spinner } from '../shared/components/Spinner';

/**
 * Renders child routes only when the user is signed in.
 * Shows configuration help or a sign-in prompt with a button (no automatic redirect).
 */
export function RequireSignedIn(): React.ReactElement {
  const { authReady, isAuthenticated, login, azureAuthConfigured } = useAuth();
  const [showConfigHint, setShowConfigHint] = useState(false);

  useEffect(() => {
    const stub = process.env.REACT_APP_AUTH_STUB === 'true';
    if (!isAuthenticated && !azureAuthConfigured && !stub) {
      setShowConfigHint(true);
    } else {
      setShowConfigHint(false);
    }
  }, [isAuthenticated, azureAuthConfigured]);

  // Wait for MSAL's initial session resolution before treating `isAuthenticated: false` as
  // "confirmed signed out" — otherwise a returning user's brief startup blip flashes the
  // sign-in prompt (and can re-trigger the admin check) before their session settles.
  if (!authReady) {
    return <Spinner label="Checking your session…" />;
  }

  if (showConfigHint) {
    return (
      <EmptyState
        title="Authentication is not configured"
        description="Add REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID to your environment, or set REACT_APP_AUTH_STUB=true for offline development."
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <EmptyState
        title="Sign in to continue"
        description="This area is available after you sign in with your account. Use the same account your organization uses for Microsoft 365 or Entra ID."
        action={
          <button type="button" className="bo-btn bo-btn-primary" onClick={login}>
            {azureAuthConfigured ? 'Sign in' : 'Continue (stub sign-in)'}
          </button>
        }
      />
    );
  }

  return <Outlet />;
}
